/**
 * Type definitions for Matrix objects
 * Matrices describe the physical/chemical composition or form of laboratory samples
 */

/**
 * Matrix object structure
 * @typedef {Object} Matrix
 * @property {number} matrixId - Unique identifier
 * @property {string} matrix - Matrix name (max 100 chars, required)
 * @property {string} [description] - Optional description (max 254 chars)
 * @property {string} [matrixType] - Optional type (e.g., "Liquid", "Solid", "Gas") (max 50 chars)
 * @property {boolean} active - Active status (default: true)
 * @property {string} createdDate - ISO date string
 * @property {string} lastMod - ISO date string
 */

/**
 * Request object for creating a new matrix
 * @typedef {Object} CreateMatrixRequest
 * @property {string} matrix - Matrix name (max 100 chars, required)
 * @property {string} [description] - Optional description (max 254 chars)
 * @property {string} [matrixType] - Optional type (e.g., "Liquid", "Solid", "Gas") (max 50 chars)
 * @property {boolean} [active] - Active status (default: true)
 */

/**
 * Matrix type options
 */
export const MATRIX_TYPES = {
    LIQUID: 'Liquid',
    SOLID: 'Solid',
    GAS: 'Gas'
};

/**
 * Common matrix codes reference
 */
export const COMMON_MATRIX_CODES = [
    { code: 'AQ', name: 'Aqueous', description: 'Unknown Liquids', type: 'Liquid' },
    { code: 'BRINE', name: 'Brine', description: 'Brine water', type: 'Liquid' },
    { code: 'DH', name: 'Drill Hole', description: 'Drill Hole', type: 'Liquid' },
    { code: 'DW', name: 'Drinking Water', description: 'Distribution samples', type: 'Liquid' },
    { code: 'GW', name: 'Ground Water', description: 'Ground Water', type: 'Liquid' },
    { code: 'HW', name: 'Haz Waste', description: 'Hazardous waste', type: 'Solid' },
    { code: 'LEACH', name: 'Leachate', description: 'Waters from in-house extractions', type: 'Liquid' },
    { code: 'LW', name: 'Liquid Waste', description: 'Non-aqueous liquids', type: 'Solid' },
    { code: 'MW', name: 'Monitor Well', description: 'Monitoring Wells', type: 'Liquid' },
    { code: 'OIL', name: 'Oil', description: 'Oil', type: 'Solid' },
    { code: 'OS', name: 'Organic Solvent', description: 'Organic Solvents', type: 'Liquid' },
    { code: 'OTHER', name: 'Other', description: 'Listed on COC as other, not defined', type: 'Liquid' },
    { code: 'PL', name: 'Pit Lake', description: 'Mining Pit Lake', type: 'Liquid' },
    { code: 'PS', name: 'Process Solution', description: 'Mining Contaminated Solutions', type: 'Liquid' },
    { code: 'SLUDGE', name: 'Sludge', description: 'Semisolid Material', type: 'Solid' },
    { code: 'SOIL', name: 'Soil', description: 'Soil', type: 'Solid' },
    { code: 'SG', name: 'Soil-Gas', description: 'Air or gas collected in bags or Orbo tubes', type: 'Air' },
    { code: 'SOLID', name: 'Solid', description: 'Solids that are not soils', type: 'Solid' },
    { code: 'SW', name: 'Storm Water', description: 'Storm water', type: 'Liquid' },
    { code: 'SF', name: 'Surface Water', description: 'Runoff, surface water', type: 'Liquid' },
    { code: 'WW', name: 'Waste Water', description: 'Waste water', type: 'Liquid' }
];

/**
 * Validate matrix data before submission
 * @param {CreateMatrixRequest} matrix - Matrix data to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateMatrix = (matrix) => {
    if (!matrix.matrix || matrix.matrix.trim().length === 0) {
        return 'Matrix name is required';
    }
    
    if (matrix.matrix.length > 100) {
        return 'Matrix name cannot exceed 100 characters';
    }
    
    if (matrix.description && matrix.description.length > 254) {
        return 'Description cannot exceed 254 characters';
    }
    
    if (matrix.matrixType && matrix.matrixType.length > 50) {
        return 'Matrix type cannot exceed 50 characters';
    }
    
    return null;
};
